<!--@format-->
<template>
    <div class="company-solutions-widget">
        <div class="widget-header">
            <h3>Assign Sold Solutions</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="disclaimer">
                <t3-alert-outline-svg class="alert-svg" color="#f30070" />
                <p class="content">
                    Only recurring revenue solutions are shown. One-time solutions are hidden
                    because they are not calculated in the solutions roadmap
                </p>
            </div>
            <div class="search-tools">
                <div class="searchbar-container">
                    <app-input
                        :value="queryOptions.searchValue"
                        placeholder="Search"
                        @input="handleSearch"
                    >
                        <div slot="inputIcon" class="input-icon">
                            <magnifying-glass-svg />
                        </div>
                    </app-input>
                </div>
                <div class="filter-buttons-container">
                    <solutions-sort-button
                        :is-open="isSortOpen"
                        :sort-options="sortOptions"
                        :applied-sort="queryOptions.sort"
                        @update:open="toggleOpenPopup($event, 'isSortOpen')"
                        @select:sort="handleSort"
                    />
                </div>
            </div>
            <div class="solutions-container">
                <loading-screen-svg v-if="loading && noItems" />
                <board-no-records-card
                    v-else-if="noRecords && noItems"
                    :title="noRecordsTitle"
                    :content="noRecordsContent"
                    img-filename="no-solutions.svg"
                >
                    <template slot="noRecordsActions">
                        <app-button @click="handleCreateNewSolution">
                            Create New Solution <o6-add-svg />
                        </app-button>
                    </template>
                </board-no-records-card>
                <template v-else>
                    <div
                        ref="solutionsList"
                        class="solutions-wrapper solutions-list"
                        @scroll="handleScroll"
                    >
                        <loading-screen-svg v-if="loading && !noItems" />
                        <board-no-records-card
                            v-else-if="noRecords && !noItems"
                            :title="noRecordsTitle"
                            :content="noRecordsContent"
                            img-filename="no-solutions.svg"
                        />
                        <solution-list-item
                            v-for="solution in availableSolutions"
                            :key="solution.id"
                            :solution="solution"
                            :type="solution.type"
                            editable
                            @click="handleAddSolution(solution)"
                        />
                    </div>
                    <div class="solutions-wrapper added-solutions">
                        <board-no-records-card
                            v-if="noItems"
                            :title="noItemsTitle"
                            :content="noItemsContent"
                            img-filename="no-solutions.svg"
                        />
                        <template v-else>
                            <div class="items-list">
                                <div v-if="!noItems" class="items-counter">{{ itemsCount }}</div>
                                <solution-list-item
                                    v-for="solution in selectedSolutions"
                                    :key="solution.id"
                                    :solution="solution"
                                    :type="solution.type"
                                    :fixed-quantity="false"
                                    show-quantity
                                    removable
                                    @remove="handleRemoveSolution(solution)"
                                    @update:item="handleUpdateItem(solution)"
                                />
                            </div>
                            <div class="items-total">
                                <span>Solutions total </span>
                                <span>{{ totalValue | currencyTwoDecimals }}</span>
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="back-button" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button @click="handleSaveSolutions"> Assign Solutions </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const BoardNoRecordsCard = () => import('@/components/board/board-no-records-card');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const MagnifyingGlassSvg = () => import('@/components/svg/magnifying-glass-svg');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const SolutionsListItem = () => import('@/components/company/company-solutions/solution-list-item');
const SolutionsSortButton = () =>
    import('@/components/sliding-widgets/document/solution-widget/solutions-sort-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const T3AlertOutlineSvg = () => import('@/components/svg/grid/t3-alert-outline-svg');

import { SolutionRoadmapService } from '@/scripts/services/solution/solution-roadmap-service';
import EventBus from '@/scripts/event-bus';
import _ from 'lodash';

export default {
    name: 'company-solutions-widget',
    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'board-no-records-card': BoardNoRecordsCard,
        'loading-screen-svg': LoadingScreenSvg,
        'magnifying-glass-svg': MagnifyingGlassSvg,
        'o6-add-svg': O6AddSvg,
        'solution-list-item': SolutionsListItem,
        'solutions-sort-button': SolutionsSortButton,
        'widget-close-svg': WidgetCloseSvg,
        't3-alert-outline-svg': T3AlertOutlineSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            totalItems: 0,
            selectedSolutions: [...this.widget.assignedSolutions],
            solutions: [],
            availableSolutions: [],
            assignQueue: [],
            removeQueue: [],
            totalValue: 0,
            noItemsTitle: 'No Solutions Assigned',
            noRecordsTitle: 'No Solutions Found',
            noItemsContent: 'Select items from the list to assign to the current Company.',
            noRecordsContent:
                'Start adding solutions in your account to assign them to the current Company.',

            queryOptions: {
                searchValue: null,
                sort: null,
                page: 1,
                itemsPerPage: 15,
            },

            isSortOpen: false,
            sortOptions: [
                {
                    id: 0,
                    label: 'Alphabetical descending',
                    property: 'Name',
                    direction: 'desc',
                },
                {
                    id: 1,
                    label: 'Alphabetical ascending',
                    property: 'Name',
                    direction: 'asc',
                },
                {
                    id: 2,
                    label: 'Price high to low',
                    property: 'MonthlyFee',
                    direction: 'desc',
                },
                {
                    id: 3,
                    label: 'Price low to high',
                    property: 'MonthlyFee',
                    direction: 'asc',
                },
            ],
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        companyId() {
            return this.widget.companyId;
        },

        itemsCount() {
            const count = this.selectedSolutions.length;
            return `${count} item${count > 1 ? 's' : ''} added`;
        },

        noItems() {
            return !this.selectedSolutions.length;
        },

        noRecords() {
            return !this.solutions.length;
        },
    },

    watch: {
        selectedSolutions: {
            immediate: true,
            deep: true,
            handler(solutions) {
                this.calculateTotalValue(solutions);
            },
        },
    },

    created() {
        EventBus.$on('solution:update', this.query);
        this.query();
    },

    beforeDestroy() {
        EventBus.$off('solution:update', this.query);
    },

    methods: {
        query() {
            this.resetResults();
            this.getSolutions(this.queryOptions);
        },

        handleLoadMore() {
            this.queryOptions.page++;
            this.getSolutions(this.queryOptions, false);
        },

        resetResults() {
            this.queryOptions.page = 1;
            this.solutions = [];
        },

        handleSearch: _.debounce(function(value) {
            this.queryOptions.searchValue = value;
            this.query();
        }, 800),

        handleSort(value) {
            if (this.queryOptions.sort && _.isEqual(this.queryOptions.sort, value)) {
                this.queryOptions.sort = null;
            } else {
                this.queryOptions.sort = value;
            }
            this.query();
        },

        toggleOpenPopup(value, popup) {
            this.closePopups();
            this[popup] = value;
        },

        closePopups() {
            this.isSortOpen = false;
        },

        handleScroll() {
            const container = this.$refs.solutionsList;
            const scrollPosition = container.scrollTop + container.clientHeight;
            const totalHeight = container.scrollHeight;

            if (
                !this.loading &&
                this.solutions.length < this.totalItems &&
                scrollPosition + 1 >= totalHeight // +1 to prevent rounding errors when zooming into the screen
            ) {
                this.handleLoadMore();
            }
        },

        createQueryParams(options) {
            const { searchValue, sort, page, itemsPerPage } = options;
            const skip = itemsPerPage && page ? `${itemsPerPage * (page - 1)}` : null;
            const top = itemsPerPage ? `${itemsPerPage}` : null;
            const orderBy = sort ? `${sort.property} ${sort.direction}` : null;
            const filter = searchValue
                ? `(contains(Name, '${encodeURIComponent(searchValue)}'))`
                : null;

            return {
                skip,
                top,
                orderBy,
                filter,
            };
        },

        getSolutions(options = {}, showLoading = true) {
            this.loading = showLoading;
            const config = this.createQueryParams(options);
            SolutionRoadmapService.getUnsoldSolutions(this.companyId, config)
                .then(({ data }) => {
                    const items = data.value;
                    this.totalItems = data['@odata.count'];
                    this.solutions = [...this.solutions, ...this.mapSolutionItems(items, 'unsold')];
                    this.getAvailableSolutions();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching solutions.',
                        {
                            error,
                        },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        mapSolutionItems(solutions, type) {
            return solutions.map((solution) => ({
                id: solution.Id,
                name: solution.Name,
                type,
                description: solution.Description,
                previewImageUrl: solution.ImageUrl,
                monthlyFee: solution.MonthlyFee,
                isBundle: Boolean(solution.Bundles && solution.Bundles.length),
                bundles: this.mapBundles(solution.Bundles),
            }));
        },

        mapBundles(bundles) {
            return bundles
                ? bundles.map((bundle) => ({
                      id: bundle.Id,
                      name: bundle.Name,
                      imageUrl: bundle.ImageUrl,
                  }))
                : [];
        },

        handleCreateNewSolution() {
            const widget = {
                name: 'solution-widget',
                accountId: this.accountId,
                action: 'Add New',
            };

            EventBus.$emit('open-widget', widget);
        },

        handleAddSolution(solution) {
            if (solution.type === 'unsold') {
                this.addUnsoldSolution(solution);
            } else {
                this.selectedSolutions.push(solution);
            }

            this.removeQueue = this.removeQueue.filter((item) => item.id !== solution.id);
            this.getAvailableSolutions();
        },

        addUnsoldSolution(solution) {
            this.$set(solution, 'quantity', 1);
            this.selectedSolutions.push(solution);
            this.assignQueue.find((item) => item.id === solution.id)
                ? _.noop()
                : this.assignQueue.push(solution);
        },

        handleRemoveSolution(solution) {
            if (solution.type === 'sold') {
                this.removeSoldSolution(solution);
            } else {
                this.selectedSolutions = this.selectedSolutions.filter(
                    (item) => item.id !== solution.id,
                );
            }
            this.assignQueue = this.assignQueue.filter((item) => item.id !== solution.id);
            this.getAvailableSolutions();
        },

        removeSoldSolution(solution) {
            this.selectedSolutions = this.selectedSolutions.filter(
                (item) => item.id !== solution.id,
            );
            const solutionIndex = this.solutions.findIndex((item) => item.id === solution.id);
            solutionIndex !== -1 ? (this.solutions[solutionIndex] = solution) : _.noop();
            this.removeQueue.push(solution);
        },

        handleUpdateItem(solution) {
            this.assignQueue.find((item) => item.id === solution.id)
                ? _.noop()
                : this.assignQueue.push(solution);
        },

        handleSaveSolutions() {
            const solutionPromises = [];
            if (this.assignQueue.length) {
                const assignPayload = {
                    CompanyId: this.companyId,
                    Solutions: this.assignQueue.map((solution) => ({
                        SolutionId: solution.id,
                        Quantity: solution.quantity,
                    })),
                };
                const assignSolutions = SolutionRoadmapService.assign(assignPayload);
                solutionPromises.push(assignSolutions);
            }

            if (this.removeQueue.length) {
                const removePayload = {
                    CompanyId: this.companyId,
                    SolutionIds: this.removeQueue.map(({ id }) => id),
                };
                const removeSolutions = SolutionRoadmapService.remove(removePayload);
                solutionPromises.push(removeSolutions);
            }

            Promise.all(solutionPromises)
                .then(() => {
                    this.$helpers.showSuccessNotification('Solutions saved successfully.');
                    EventBus.$emit('company-solutions:refresh');
                    EventBus.$emit('solutions-roadmap:refresh');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your solutions.',
                        {
                            error,
                        },
                    );
                })
                .finally(() => {
                    this.closeWidget();
                });
        },

        calculateTotalValue(items) {
            this.totalValue = items.reduce(
                (acc, { monthlyFee, quantity }) => acc + monthlyFee * quantity,
                0,
            );
        },

        getAvailableSolutions() {
            this.availableSolutions = _.differenceBy(this.solutions, this.selectedSolutions, 'id');
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.company-solutions-widget .content-wrapper {
    padding: 0px !important;
}

.company-solutions-widget .disclaimer {
    display: flex;
    height: 56px;
    padding: 16px 24px 0px;
}

.company-solutions-widget .disclaimer .alert-svg {
    min-width: 40px;
    width: 40px;
    height: 40px;
    margin-right: 12px;
}

.company-solutions-widget .disclaimer .alert-svg path.a {
    fill: transparent;
    stroke: #f30070;
}

.company-solutions-widget .disclaimer .content {
    color: #f30070;
    font: 400 16px/20px 'Roboto';
    letter-spacing: 0px;
    margin: 0;
}

.company-solutions-widget .search-tools {
    border-bottom: 2px solid #e9e9ef;
    display: flex;
    gap: 12px;
    height: 72px;
    justify-content: space-between;
    padding: 16px 24px;
}

.company-solutions-widget .search-tools .searchbar-container {
    flex: 1;
}

.company-solutions-widget .search-tools .searchbar-container .app-input .icon-container {
    top: 8px;
    right: 12px;
}

.company-solutions-widget .solutions-container {
    display: flex;
    height: calc(100% - 130px);
    overflow: hidden;
}

.company-solutions-widget .solutions-container .board-no-records-card,
.company-solutions-widget .solutions-container .loading-screen {
    margin: auto;
}

.company-solutions-widget .solutions-container .solutions-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    width: 376px;
}

.company-solutions-widget .solutions-container .solutions-list {
    overflow: auto;
    padding: 16px 0px;
}

.company-solutions-widget
    .solutions-container
    .solutions-wrapper
    .solution-list-item:not(:last-child) {
    border-bottom: 2px solid #e9e9ef;
}

.company-solutions-widget .solutions-container .added-solutions {
    border-left: 1px solid #e9e9ef;
    overflow: hidden;
    width: 312px;
}

.company-solutions-widget .solutions-container .added-solutions .board-no-records-card {
    width: 80%;
}

.company-solutions-widget .solutions-container .added-solutions .board-no-records-card h1,
.company-solutions-widget .solutions-container .board-no-records-card h1 {
    font: normal normal bold 22px/21px 'Nunito';
    letter-spacing: 0px;
    color: #382153;
}

.company-solutions-widget .solutions-container .board-no-records-card .content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 70%;
}

.company-solutions-widget .solutions-container .added-solutions .board-no-records-card .content {
    font-size: 14px;
}

.company-solutions-widget .solutions-container .added-solutions .items-list {
    border-bottom: 2px solid #e9e9ef;
    height: calc(100% - 64px);
    overflow: auto;
    padding-bottom: 16px;
}

.company-solutions-widget
    .solutions-container
    .added-solutions
    .items-list
    .solution-list-item:hover {
    background-color: unset;
    cursor: default;
}

.company-solutions-widget .solutions-container .added-solutions .items-list .items-counter {
    border-bottom: 1px solid #cac6cb;
    color: #382153;
    font-weight: 500;
    padding: 16px;
}

.company-solutions-widget .solutions-container .added-solutions .items-total {
    color: #382153;
    display: flex;
    height: 64px;
    overflow: auto;
    padding: 16px;
    align-items: flex-end;
    justify-content: flex-end;
}

.company-solutions-widget .solutions-container .added-solutions .items-total > span:first-child {
    text-align: left;
    font: normal normal normal 14px/24px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
    margin-right: 8px;
    line-height: 10px;
}

.company-solutions-widget .solutions-container .added-solutions .items-total > span:last-child {
    font: normal normal 500 32px/24px 'Roboto';
}

.company-solutions-widget .options-container .button-container .app-button svg,
.company-solutions-widget .solutions-container .board-no-records-card .app-button svg {
    margin-left: 10px;
}

.company-solutions-widget .options-container .button-container .app-button svg .a,
.company-solutions-widget .solutions-container .board-no-records-card .app-button svg .a {
    fill: #ffffff;
}

.company-solutions-widget .app-input .input-icon .magnifying-glass-svg path {
    fill: #f30070;
}

/*Customize Scrollbar for everything inside solutions container */
.company-solutions-widget .content-wrapper *::-webkit-scrollbar {
    width: 16px;
}

.company-solutions-widget .content-wrapper *::-webkit-scrollbar-track {
    border-left: 1px solid #e9e9ef;
}

.company-solutions-widget .content-wrapper *::-webkit-scrollbar-thumb {
    background-color: #cac6cb;
    border-radius: 16px;
    border: 3px solid transparent;
    background-clip: content-box;
}
</style>
